import React from 'react';
import './markdown.css';

/**
 * Factory to create a markdown-to-jsx node processor that detects @@<uuid>@@ tokens
 * and replaces them with clickable spans that invoke the provided click handler.
 */
export function makeUUIDProcessor(handleUUIDClick) {
  return function processNode(node) {
    if (typeof node === 'string') {
      const uuidRegex = /@@([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})@@/g;
      const parts = [];
      let lastIndex = 0;
      let match;
      while ((match = uuidRegex.exec(node)) !== null) {
        if (match.index > lastIndex) {
          parts.push(node.substring(lastIndex, match.index));
        }
        const uuid = match[1];
        parts.push(
          <span
            key={`${match.index}-${uuid}`}
            className="clickable-uuid"
            onClick={(e) => {
              e.stopPropagation();
              if (typeof handleUUIDClick === 'function') handleUUIDClick(uuid);
            }}
            title={`View Exploration Log (${uuid})`}
          >
            {`«Explore: ${uuid.substring(0, 8)}...»`}
          </span>
        );
        lastIndex = uuidRegex.lastIndex;
      }
      if (lastIndex < node.length) {
        parts.push(node.substring(lastIndex));
      }
      return parts.length > 0 ? <>{parts}</> : node;
    } else if (React.isValidElement(node) && node.props?.children) {
      return React.cloneElement(node, {
        ...node.props,
        children: React.Children.map(node.props.children, processNode),
      });
    }
    return node;
  };
}
    
/** Build markdown-to-jsx options with file link interception */
export function buildMarkdownOptions({
  processNode,
  onFileLink,
  authUsername,
  BASE_URL,
  setIsViewerOpen,
  setViewingFilePath
} = {}) {
  const safeProcess = (node) => (typeof processNode === 'function' ? processNode(node) : node);

  console.log('buildMarkdownOptions called with:', { authUsername, BASE_URL });

  // Helper: normalize incoming href/src values
  const stripSchemeAndDots = (raw) => {
    if (!raw || typeof raw !== 'string') return '';
    let s = raw.trim();
    // remove leading './'
    if (s.startsWith('./')) s = s.substring(2);
    // strip sandbox:/, file://, vscode-webview://, etc., keeping only the path part
    const schemeMatch = s.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/*/);
    if (schemeMatch) s = s.substring(schemeMatch[0].length);
    // de-root to relative
    if (s.startsWith('/')) s = s.substring(1);
    return s;
  };

  const isAgentManaged = (p) => {
    const lower = (p || '').toLowerCase();
    return lower.startsWith('home/') || lower.startsWith('charts/') || lower.startsWith('./');
  };

  const getHomeRelative = (p) => {
    const lower = (p || '').toLowerCase();
    if (lower.startsWith('home/')) return p.substring(5);
    if (lower.startsWith('./')) return p.substring(2);
    return '';
  };

  return {
    forceBlock: true,
    overrides: {
      // Standard text containers - run through processNode if provided
      p: { component: ({ children, ...props }) => <p {...props}>{React.Children.map(children, safeProcess)}</p> },
      pre: { component: ({ children, ...props }) => <pre {...props}>{React.Children.map(children, safeProcess)}</pre> },
      code: { component: ({ children, ...props }) => <code {...props}>{React.Children.map(children, safeProcess)}</code> },
      li: {
        component: ({ children, ...props }) => (
          <li {...props}>{React.Children.map(children, child => safeProcess(child))}</li>
        )
      },
      span: {
        component: ({ children, ...props }) => (
          <span {...props}>{React.Children.map(children, child => safeProcess(child))}</span>
        )
      },

      // Anchor override: handle agent-managed paths, images, and documents
      a: {
        component: ({ children, href, ...props }) => {
          // Normalize early and prefer File Explorer if handler provided.
          // NOTE: markdown-to-jsx usually provides string hrefs, but we occasionally see
          // non-strings (e.g. objects) coming through, which must not crash rendering.
          const hrefStr = (typeof href === 'string') ? href : (href === null || href === undefined ? '' : String(href));
          const hrefLower = hrefStr ? hrefStr.toLowerCase() : '';
          const hrefNorm = stripSchemeAndDots(hrefStr);
          const hrefNormLower = hrefNorm ? hrefNorm.toLowerCase() : '';
          if (
            hrefStr && onFileLink && (
              hrefLower.startsWith('./') ||
              hrefNormLower.startsWith('home/') ||
              hrefNormLower.startsWith('charts/')
            )
          ) {
            const filenameInline = (hrefNorm || hrefStr).split('/').pop() || 'file';
            return (
              <span
                className="agent-file-link-wrapper"
                role="button"
                title={`Open in File Explorer: ${filenameInline}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onFileLink(hrefStr);
                }}
                {...props}
              >
                <span className="agent-file-link">
                  {React.Children.map(children, safeProcess)}
                </span>
                <svg className="agent-file-eye" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-label="Opens in viewer">
                  <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8" fill="none" stroke="currentColor" />
                  <circle cx="8" cy="8" r="3.5" fill="none" stroke="currentColor" />
                </svg>
              </span>
            );
          }

          if (!hrefStr) {
            // Keep as a normal anchor, but don't pass through non-string href values.
            return <a href={undefined} {...props}>{React.Children.map(children, child => safeProcess(child))}</a>;
          }

          let normalizedHrefForApi = hrefNorm;
          const isAgentManagedPath = isAgentManaged(normalizedHrefForApi);

          if (isAgentManagedPath && authUsername) {
            const filename = normalizedHrefForApi.substring(normalizedHrefForApi.lastIndexOf('/') + 1);
            const fileType = filename.split('.').pop()?.toLowerCase() || '';

            const pathRelativeToHome = getHomeRelative(normalizedHrefForApi);
            // For legacy 'charts/' paths, pathRelativeToHome will be empty.

            const isDocx = fileType === 'docx';
            const isPdf = fileType === 'pdf';
            const isCsv = fileType === 'csv';
            const isTxt = fileType === 'txt';
            const isTargetDocumentTypeForModal = (isDocx || isPdf || isCsv || isTxt) && pathRelativeToHome;

            const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.ico'];
            const isImage = imageExtensions.some(ext => normalizedHrefForApi.toLowerCase().endsWith(ext));

            // Always route agent-managed files through the API. If BASE_URL is empty, use relative "/api" (dev proxy will handle it).
            const base = (BASE_URL || '').replace(/\/$/, '');
            const apiUrl = `${base}/api/file_explorer/view_user_file/${normalizedHrefForApi}`;

            if (isImage && pathRelativeToHome) { // Link to an image under Home/, open in viewer
              const altTextContent = (typeof children === 'string') ? children : (Array.isArray(children) ? children.filter(c => typeof c === 'string').join('') : filename);
              return (
                <img
                  src={apiUrl}
                  alt={altTextContent || filename}
                  className="agent-generated-image"
                  title={`View Image: ${filename} (Click to open in viewer)`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (typeof setViewingFilePath === 'function') setViewingFilePath(pathRelativeToHome);
                    if (typeof setIsViewerOpen === 'function') setIsViewerOpen(true);
                  }}
                  onError={(e) => { console.error(`Failed to load image: ${apiUrl}`); e.target.style.display = 'none'; }}
                />
              );
            } else if (isImage) { // Image not under Home/ (e.g., charts/) – prefer File Explorer if available
              const altTextContent = (typeof children === 'string') ? children : (Array.isArray(children) ? children.filter(c => typeof c === 'string').join('') : filename);
              if (typeof onFileLink === 'function') {
                return (
                  <span
                    className="agent-file-link"
                    role="button"
                    title={`Open in File Explorer: ${filename}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onFileLink(normalizedHrefForApi);
                    }}
                  >
                    <img src={apiUrl} alt={altTextContent} className="agent-generated-image" onError={(e) => { console.error(`Failed to load image: ${apiUrl}`); e.target.style.display = 'none'; }} />
                  </span>
                );
              }
              return (
                <a href={apiUrl} target="_blank" rel="noopener noreferrer" title={`View Image: ${filename} (Opens in new tab)`}>
                  <img src={apiUrl} alt={altTextContent} className="agent-generated-image" onError={(e) => { console.error(`Failed to load image: ${apiUrl}`); e.target.style.display = 'none'; }} />
                </a>
              );
            } else if (isTargetDocumentTypeForModal) { // Documents that open in viewer
              const linkTextContent = children ? React.Children.map(children, child => safeProcess(child)) : filename;
              return (
                <span
                  className="agent-file-link-wrapper"
                  role="button"
                  title={`Open in File Explorer: ${filename}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (typeof setViewingFilePath === 'function') setViewingFilePath(pathRelativeToHome);
                    if (typeof setIsViewerOpen === 'function') setIsViewerOpen(true);
                  }}
                >
                  <span className="agent-file-link">{linkTextContent}</span>
                  <svg className="agent-file-eye" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-label="Opens in viewer">
                    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8" fill="none" stroke="currentColor" />
                    <circle cx="8" cy="8" r="3.5" fill="none" stroke="currentColor" />
                  </svg>
                </span>
              );
            } else { // Default: open File Explorer for other agent-managed files with eye icon
              const linkTextContent = children ? React.Children.map(children, child => safeProcess(child)) : filename;
              const openHandler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof onFileLink === 'function') onFileLink(normalizedHrefForApi);
              };
              return (
                <span
                  className="agent-file-link-wrapper"
                  role="button"
                  title={`Open in File Explorer: ${filename}`}
                  onClick={openHandler}
                >
                  <span className="agent-file-link">{linkTextContent}</span>
                  <svg className="agent-file-eye" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-label="Opens in viewer">
                    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8" fill="none" stroke="currentColor" />
                    <circle cx="8" cy="8" r="3.5" fill="none" stroke="currentColor" />
                  </svg>
                </span>
              );
            }
          } else if (isAgentManagedPath && !authUsername) {
            const filename = normalizedHrefForApi.substring(normalizedHrefForApi.lastIndexOf('/') + 1);
            return <span title="Cannot display file: Authentication required. Please log in.">[File: {filename}]</span>;
          }

          return <a href={href} {...props}>{React.Children.map(children, child => safeProcess(child))}</a>;
        }
      },

      // Image override: route agent-managed images through API and optionally open viewer for Home/
      img: {
        component: ({ src, alt, ...props }) => {
          if (!src) return <img src={src} alt={alt} {...props} />;

          let normalizedSrcForApi = stripSchemeAndDots(src);
          const isAgentManagedPath = isAgentManaged(normalizedSrcForApi);

          if (isAgentManagedPath && authUsername) {
            // Always route agent-managed images through the API. If BASE_URL is empty, use relative "/api" (dev proxy will handle it).
            const base = (BASE_URL || '').replace(/\/$/, '');
            const apiUrl = `${base}/api/file_explorer/view_user_file/${normalizedSrcForApi}`;
            const filename = normalizedSrcForApi.substring(normalizedSrcForApi.lastIndexOf('/') + 1);
            if (normalizedSrcForApi.toLowerCase().startsWith('home/')) {
              const pathRelativeToHomeForViewer = getHomeRelative(normalizedSrcForApi);
              return (
                <img
                  src={apiUrl}
                  alt={alt || filename}
                  className="agent-generated-image"
                  title={`View Image: ${filename} (Click to open in viewer)`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (typeof setViewingFilePath === 'function') setViewingFilePath(pathRelativeToHomeForViewer);
                    if (typeof setIsViewerOpen === 'function') setIsViewerOpen(true);
                  }}
                  onError={(e) => { console.error(`Failed to load image: ${apiUrl}`); e.target.style.display = 'none'; }}
                  {...props}
                />
              );
            } else {
              // For charts/ and other agent-managed paths not under Home, prefer File Explorer if handler exists
              if (typeof onFileLink === 'function') {
                return (
                  <img
                    src={apiUrl}
                    alt={alt || filename}
                    className="agent-generated-image"
                    title={`Open in File Explorer: ${filename}`}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onFileLink(normalizedSrcForApi);
                    }}
                    onError={(e) => { console.error(`Failed to load image: ${apiUrl}`); e.target.style.display = 'none'; }}
                    {...props}
                  />
                );
              }
              return (
                <img
                  src={apiUrl}
                  alt={alt || filename}
                  className="agent-generated-image"
                  title={`Image: ${filename}`}
                  onError={(e) => { console.error(`Failed to load image: ${apiUrl}`); e.target.style.display = 'none'; }}
                  {...props}
                />
              );
            }
          } else if (isAgentManagedPath && !authUsername) {
            const filename = normalizedSrcForApi.substring(normalizedSrcForApi.lastIndexOf('/') + 1);
            return <span title="Cannot display image: Authentication required. Please log in.">[Image: {alt || filename}]</span>;
          }
          // Default img rendering
          return <img src={src} alt={alt} {...props} />;
        }
      },

      // Table styling
      table: { component: 'table', props: { className: 'markdown-table' } },
      thead: { component: 'thead', props: { className: 'markdown-thead' } },
      tbody: { component: 'tbody', props: { className: 'markdown-tbody' } },
      tr: { component: 'tr', props: { className: 'markdown-tr' } },
      th: { component: 'th', props: { className: 'markdown-th' } },
      td: { component: 'td', props: { className: 'markdown-td' } },
    }
  };
}

export default buildMarkdownOptions;